<script>
  import { onMount } from "svelte";
  import api from "../../services/api.js";

  let orders = [];
  let loading = true;
  let error = null;
  let statusFilter = "";

  onMount(async () => {
    await fetchOrders();
  });

  async function fetchOrders() {
    loading = true;
    try {
      const response = await api.getOrders(statusFilter || null);
      orders = response.orders || response || [];
    } catch (e) {
      error = e.message;
    }
    loading = false;
  }

  async function cancelOrder(orderId) {
    if (!confirm("Are you sure you want to cancel this order?")) return;
    try {
      await api.cancelOrder(orderId);
      await fetchOrders();
    } catch (e) {
      error = e.message;
    }
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleString();
  }

  function getStatusBadge(status) {
    switch (status) {
      case "filled": return "badge-success";
      case "partially_filled": return "badge-info";
      case "new":
      case "pending_new":
      case "accepted": return "badge-warning";
      case "canceled":
      case "expired":
      case "rejected": return "badge-danger";
      default: return "badge-neutral";
    }
  }
</script>

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Orders</h1>
  <p class="text-sm text-ink-secondary mt-1">Review and manage your order history.</p>
</header>

{#if error}
  <div class="banner banner-danger mb-4 flex items-start justify-between gap-3">
    <span>{error}</span>
    <button on:click={() => error = null} class="font-semibold hover:text-red-900" aria-label="Dismiss">&times;</button>
  </div>
{/if}

<div class="flex flex-wrap items-center gap-3 mb-4">
  <select bind:value={statusFilter} on:change={fetchOrders} class="select !w-auto !py-2">
    <option value="">All Orders</option>
    <option value="open">Open</option>
    <option value="closed">Closed</option>
    <option value="all">All Statuses</option>
  </select>
  <button on:click={fetchOrders} class="btn btn-ghost btn-sm" title="Refresh orders">
    <i class="fas fa-sync-alt"></i> Refresh
  </button>
</div>

<div class="card overflow-hidden">
  <div class="overflow-x-auto">
    <table class="w-full border-collapse">
      <thead>
        <tr>
          <th class="th">Symbol</th>
          <th class="th">Side</th>
          <th class="th">Type</th>
          <th class="th text-right">Qty / Notional</th>
          <th class="th">Status</th>
          <th class="th">Created</th>
          <th class="th text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#if loading}
          {#each Array(5) as _}
            <tr>
              <td class="td"><div class="skeleton h-4 w-14"></div></td>
              <td class="td"><div class="skeleton h-4 w-10"></div></td>
              <td class="td"><div class="skeleton h-4 w-14"></div></td>
              <td class="td"><div class="skeleton h-4 w-16 ml-auto"></div></td>
              <td class="td"><div class="skeleton h-5 w-16 rounded-full"></div></td>
              <td class="td"><div class="skeleton h-4 w-32"></div></td>
              <td class="td"><div class="skeleton h-4 w-14 ml-auto"></div></td>
            </tr>
          {/each}
        {:else if orders.length === 0}
          <tr>
            <td colspan="7" class="td">
              <div class="py-10 text-center">
                <i class="fas fa-inbox text-2xl text-ink-tertiary mb-3"></i>
                <p class="text-sm text-ink-secondary">No orders found</p>
              </div>
            </td>
          </tr>
        {:else}
          {#each orders as order}
            <tr class="hover:bg-canvas">
              <td class="td">
                <span class="text-sm font-semibold text-ink">{order.symbol}</span>
              </td>
              <td class="td">
                <span class="{order.side === 'buy' ? 'text-emerald-600' : 'text-red-600'} font-medium capitalize">
                  {order.side}
                </span>
              </td>
              <td class="td capitalize">
                {order.type}
              </td>
              <td class="td text-right tnum">
                {order.notional ? `$${order.notional}` : order.qty}
              </td>
              <td class="td">
                <span class="badge {getStatusBadge(order.status)}">
                  {order.status}
                </span>
              </td>
              <td class="td text-ink-secondary tnum">
                {formatDate(order.created_at)}
              </td>
              <td class="td text-right">
                {#if order.status === "new" || order.status === "pending_new" || order.status === "accepted"}
                  <button on:click={() => cancelOrder(order.id)} class="btn btn-ghost btn-sm text-danger hover:text-red-700">
                    Cancel
                  </button>
                {:else}
                  <span class="text-ink-tertiary">-</span>
                {/if}
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</div>
